/**
 * Общие правила показа сессии автоарбитража — для списка /auto и страницы
 * /routes/:id.
 *
 * 29.09: две страницы показывали одну и ту же сессию по-разному. Список брал
 * статус из is_paused («PAUSED»), детали — из движкового state («OPENED»);
 * детали читали числа закрытий не из того уровня события («Закрыли $— · PnL
 * +$0.00» при реальных −$1.95 в карточке) и тянули события удалённой сессии
 * с тем же номером. Всё, что должно совпадать на обеих страницах, живёт здесь.
 */
import type { Route } from '../types';
import { exchangeLabel } from '../components/ExchangeIcon';
import i18n from '../i18n';

const t = i18n.getFixedT(null, 'routeView');

export const fmtUsd = (v: unknown, dp = 2): string => {
  const n = Number(v); if (v == null || !Number.isFinite(n)) return '—';
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(dp)}`;
};
/** «+$0.74» / «-$1.95». Знак — перед долларом, а не «$-5.2760». */
export const fmtSignedUsd = (v: unknown, dp = 2): string => {
  const n = Number(v); if (v == null || !Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${fmtUsd(n, dp)}`;
};
const fmtPct = (v: unknown, dp = 3): string => {
  const n = Number(v); if (v == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(dp)}%`;
};

/** Разбор уже открытого хеджа («из позиции», имя с ↩): только выходы. */
export const isUnwindRoute = (r: Pick<Route, 'name'> & { unwind?: unknown } | null | undefined): boolean =>
  Boolean(r && ((r as any).unwind || r.name?.startsWith('↩')));

export const routeHasPosition = (r: Route): boolean =>
  (Number(r.filled_qty) || 0) > 0 || (Number(r.filled_usd) || 0) > 0;

export type RouteStatusKey = 'error' | 'paused' | 'stopped' | 'closed' | 'idle' | 'opening' | 'opened' | 'closing';
export interface RouteStatus { key: RouteStatusKey; label: string; cls: string; title: string }

const STATUS_CLS: Record<RouteStatusKey, string> = {
  error:   'bg-trade-red/20 text-trade-red',
  paused:  'bg-yellow-500/20 text-yellow-500',
  stopped: 'bg-muted text-muted-foreground',
  closed:  'bg-muted text-muted-foreground',
  idle:    'bg-muted text-muted-foreground',
  opening: 'bg-yellow-500/20 text-yellow-500',
  opened:  'bg-trade-green/20 text-trade-green',
  closing: 'bg-yellow-500/20 text-yellow-500',
};

/** Единый статус сессии. Приоритет: ошибка → пауза → снята → что делает движок.
 *
 *  Движковый state на паузе не обновляется (цикл спит), поэтому «OPENED» у
 *  сессии на паузе значит лишь «позиция есть», а не «работает» — отсюда
 *  «на паузе · в позиции». */
export function routeStatus(r: Route): RouteStatus {
  const pos = routeHasPosition(r);
  const mk = (key: RouteStatusKey, label: string, title: string): RouteStatus =>
    ({ key, label, title, cls: STATUS_CLS[key] });
  if (r.state === 'ERROR') {
    return mk('error', r.is_paused ? t('status.errorPaused') : t('status.error'),
      r.last_error ? t('status.errorTitleWith', { error: r.last_error }) : t('status.errorTitle'));
  }
  if (r.is_paused) {
    return mk('paused', pos ? t('status.pausedInPosition') : t('status.paused'),
      pos ? t('status.pausedInPositionTitle')
          : t('status.pausedTitle'));
  }
  if (!r.is_active) {
    return pos ? mk('stopped', t('status.stopped'), t('status.stoppedTitle'))
               : mk('closed', t('status.closed'), t('status.closedTitle'));
  }
  switch (r.state) {
    case 'OPENING': return mk('opening', t('status.opening'), t('status.openingTitle'));
    case 'OPENED':  return mk('opened', t('status.opened'), t('status.openedTitle'));
    case 'CLOSING': return mk('closing', t('status.closing'), t('status.closingTitle'));
    default:        return mk('idle', t('status.idle'), t('status.idleTitle'));
  }
}

/** «↩ H100USDT lighter→bitget» → «↩ H100USDT Lighter→Bitget». Имя сессии
 *  собирает бэкенд из сырых id площадок; меняем только известные id. */
export function routeDisplayName(name: string | null | undefined): string {
  if (!name) return '';
  return name.replace(/[a-z][a-z0-9_]*/g, (tok) => {
    const lbl = exchangeLabel(tok);
    return lbl && lbl !== tok.toUpperCase() ? lbl : tok;
  });
}

/** Режим сессии по-русски: «реал» — настоящие ордера, «бумага» — симуляция. */
export const modeLabel = (mode?: string | null): string => (mode === 'live' ? t('mode.live') : t('mode.paper'));

/** Событие маршрута в едином виде.
 *
 *  Журнал в памяти отдаёт {kind, accepted, reason, data}, а таблица событий в
 *  SQLite — ВЕСЬ конверт в `payload`: {payload:{kind, accepted, reason,
 *  data:{usd, pnl_usd…}}}. Страница сессии читала `data ?? proposal` на
 *  верхнем уровне, получала {} — отсюда «Закрыли $— · PnL +$0.00» и «Выход
 *  пропущен: —» (accepted тоже лежит внутри payload). */
export interface RouteEvent {
  ts: number;
  route_id?: number;
  kind: string;
  direction?: string;
  accepted?: boolean;
  reason?: string;
  data: Record<string, any>;
  id?: number;
}
export function normalizeRouteEvent(ev: any): RouteEvent {
  const p = (ev && typeof ev.payload === 'object' && ev.payload) || {};
  const data = ev?.data ?? p.data ?? ev?.proposal ?? p.proposal ?? {};
  return {
    ts: Number(ev?.ts ?? p.ts) || 0,
    route_id: ev?.route_id ?? p.route_id,
    kind: String(ev?.kind ?? p.kind ?? ''),
    direction: ev?.direction ?? p.direction,
    accepted: ev?.accepted ?? p.accepted,
    reason: ev?.reason ?? p.reason,
    data: (data && typeof data === 'object') ? data : {},
    id: ev?.id,
  };
}

/** Момент создания сессии, мс. Номера сессий переиспользуются после удаления,
 *  а строки событий в SQLite остаются привязаны к номеру: у #1 в базе лежат
 *  закрытия удалённой #1 от 25.09 — их не показываем. */
export const routeBornMs = (r: Pick<Route, 'created_at'> | null | undefined): number | null => {
  const t = Date.parse(r?.created_at || '');
  return Number.isFinite(t) ? t : null;
};

export const CLOSE_KINDS = new Set(['paper_close_filled', 'live_close_filled']);
export const OPEN_KINDS = new Set(['paper_open_filled', 'live_open_filled']);

/** Сколько реально исполнила каждая нога при закрытии.
 *
 *  H100 #1 27.09: все 11 «выходов» — `long_qty 0, short_qty 10`: откупался
 *  только шорт Bitget, лонг Lighter не продавался. Движок записал их как
 *  обычные закрытия с оценочным PnL по спреду, но хедж не закрывался —
 *  поэтому учёт позиции (min ног) и не уменьшался. Такие строки помечаем. */
export function closeLegs(d: Record<string, any>) {
  const lq = Number(d.long_qty), sq = Number(d.short_qty);
  const known = Number.isFinite(lq) && Number.isFinite(sq);
  const oneSided = known && (lq > 0) !== (sq > 0);
  return { longQty: known ? lq : null, shortQty: known ? sq : null, oneSided,
           hedgedQty: known ? Math.min(lq, sq) : null };
}

/** Подсказка к выходу одной ногой. Функция, а не константа: текст берётся на
 *  момент показа, а не один раз при загрузке модуля. */
export const oneSidedHint = (): string => t('oneSidedHint');

export type Tone = 'green' | 'red' | 'yellow' | 'muted';

/** Событие → строка ленты. Один маппер на обе страницы. */
export function formatRouteEvent(e: RouteEvent, opts?: { tokens?: boolean }): { icon: string; text: string; tone: Tone; title?: string } {
  const d = e.data || {};
  const sp = d.spread_open ?? d.spread_pct ?? d.current_spread;
  const reason = e.reason || '—';
  switch (e.kind) {
    case 'paper_open_filled':
    case 'live_open_filled':
      return { icon: '↗', tone: 'green',
        text: t('events.opened', { usd: fmtUsd(d.usd, 0) })
              + (d.filled_usd != null
                 // При учёте в монетах (size_mode=tokens) total_size — монеты, а
                 // filled_usd — доллары: «всего $0 / $1591» сравнивало разное.
                 ? (d.total_size && !opts?.tokens
                    ? t('events.openedTotalOf', { filled: fmtUsd(d.filled_usd, 0), total: fmtUsd(d.total_size, 0) })
                    : t('events.openedTotal', { filled: fmtUsd(d.filled_usd, 0) }))
                 : '')
              + (Number.isFinite(Number(d.spread_open)) ? t('events.bySpread', { spread: fmtPct(d.spread_open) }) : '') };
    case 'paper_close_filled':
    case 'live_close_filled': {
      const pnl = Number(d.pnl_usd);
      const hasPnl = d.pnl_usd != null && Number.isFinite(pnl);
      const legs = closeLegs(d);
      const spTxt = Number.isFinite(Number(d.exit_spread)) ? t('events.bySpread', { spread: fmtPct(d.exit_spread) }) : '';
      const pnlTxt = hasPnl ? fmtSignedUsd(pnl) : '—';
      if (legs.oneSided) {
        return { icon: '⚠', tone: 'red', title: oneSidedHint(),
          text: t('events.closedOneSided', { longQty: String(legs.longQty), shortQty: String(legs.shortQty),
                                             usd: fmtUsd(d.usd, 0), spread: spTxt, pnl: pnlTxt }) };
      }
      return { icon: '↘', tone: !hasPnl ? 'muted' : pnl >= 0 ? 'green' : 'red',
        text: t('events.closed', { usd: fmtUsd(d.usd, 0), pnl: pnlTxt, spread: spTxt }) };
    }
    case 'open_proposal':
      return e.accepted
        ? { icon: '⚡', tone: 'yellow', text: t('events.openSignal', { spread: fmtPct(sp) }) }
        : { icon: '·', tone: 'muted',  text: t('events.openSkipped', { reason }) };
    case 'close_proposal':
      return e.accepted
        ? { icon: '⚡', tone: 'yellow', text: t('events.closeSignal', { spread: fmtPct(d.current_spread), threshold: fmtPct(d.exit_th) }) }
        : { icon: '·', tone: 'muted',  text: t('events.closeSkipped', { reason }) };
    case 'scale_in_proposal':
      return e.accepted
        ? { icon: '+', tone: 'green', text: t('events.scaleIn', { spread: fmtPct(sp) }) }
        : { icon: '·', tone: 'muted',  text: t('events.scaleInSkipped', { reason }) };
    case 'take_profit_triggered':
      return { icon: '🎯', tone: 'green', text: t('events.takeProfit', { pnl: fmtPct(d.unreal_pct ?? d.pnl_pct, 2) }) };
    case 'stop_loss_triggered':
      return { icon: '🛑', tone: 'red',   text: t('events.stopLoss', { pnl: fmtPct(d.unreal_pct ?? d.pnl_pct, 2) }) };
    case 'target_reached':
      return { icon: '✓', tone: 'muted', text: t('events.targetReached') };
    case 'leg_imbalance':
      return { icon: '⚠', tone: 'red', text: e.reason
        ? t('events.legImbalanceReason', { reason: e.reason.replace(/^ноги [^:]{3,40}:\s*/i, '') })
        : t('events.legImbalanceQty', { longQty: String(d.long_qty ?? '?'), shortQty: String(d.short_qty ?? '?') }) };
    case 'live_position_resync':
      return { icon: '⟳', tone: 'yellow',
        text: t('events.resync', { oldQty: String(d.old_qty ?? '?'), newQty: String(d.new_qty ?? '?') })
              + (d.manual ? t('events.resyncManual') : '')
              + (d.exch_long_qty != null && d.exch_short_qty != null
                 && Math.abs(Number(d.exch_long_qty) - Number(d.exch_short_qty)) > 1e-9
                 ? t('events.resyncVenues', { longQty: String(d.exch_long_qty), shortQty: String(d.exch_short_qty) }) : '') };
    case 'live_position_drift':
      return { icon: '⚠', tone: 'red', text: t('events.drift', { botQty: String(d.bot_qty), exchQty: String(d.exch_hedged_qty), drift: fmtPct(d.drift_pct, 1) }) };
    case 'unwind_extra_closed':
      return { icon: '✓', tone: 'muted', text: e.reason && e.reason !== 'излишек закрыт' && e.reason !== 'надлишок закрито'
        ? t('events.extraClosedReason', { reason: e.reason }) : t('events.extraClosed') };
    case 'unwind_extra_skipped':
      return { icon: '·', tone: 'muted', text: t('events.extraSkipped', { reason }) };
    case 'close_dust_cleared':
      return { icon: '·', tone: 'muted', text: t('events.dustCleared', { reason }) };
    case 'orphan_leg_closed':
      return { icon: '⚠', tone: 'yellow', text: t('events.orphanClosed', { leg: String(d.leg || ''), exchange: String(d.exchange || ''), qty: String(d.qty ?? '') }) };
    case 'orphan_leg_close_failed':
      return { icon: '⚠', tone: 'red', text: t('events.orphanCloseFailed', { reason }) };
    case 'live_close_slippage':
      return { icon: '⚠', tone: 'yellow',
        text: Number.isFinite(Number(d.trigger_spread)) && Number.isFinite(Number(d.realized_spread))
          ? t('events.closeSlippage', { expected: fmtPct(d.trigger_spread), realized: fmtPct(d.realized_spread) })
          : t('events.closeSlippageReason', { reason }) };
    case 'live_open_slippage':
      return { icon: '⚠', tone: 'yellow',
               text: t('events.openSlippage', { expected: fmtPct(d.expected_spread), realized: fmtPct(d.realized_spread),
                                                tolerance: fmtPct(d.tolerance_pct, 2) }) };
    case 'margin_mode_not_applied':
      return { icon: '⚠', tone: 'yellow', text: t('events.marginModeNotApplied', { reason }) };
    case 'leverage_skipped_foreign_position':
      return { icon: '·', tone: 'muted', text: t('events.leverageSkippedForeign', { reason }) };
    case 'leverage_capped_by_venue':
      return { icon: '⚠', tone: 'yellow',
               text: t('events.leverageCapped', {
                       exchange: d.exchange ? exchangeLabel(String(d.exchange)) : t('events.leverageCappedExchange'),
                       symbol: d.symbol || t('events.leverageCappedPair'),
                       requested: String(d.requested), allowed: String(d.allowed) })
                     + (Number(d.allowed) <= 1 ? t('events.leverageCappedMargin') : '') };
    case 'live_leverage_autocut':
      return { icon: '⚠', tone: 'yellow', text: t('events.leverageAutocut') };
    case 'config_conflict':
      return { icon: '⚠', tone: 'red', text: t('events.configConflict', { exit: fmtPct(d.exit_th, 2), entry: fmtPct(d.entry_th, 2) }) };
    case 'auto_close_partial_done':
      return { icon: '✓', tone: 'muted', text: t('events.partialCloseDone', { cap: String(d.cap ?? '?') }) };
    case 'auto_adjust':
      return { icon: '⚙', tone: 'muted',
               text: t('events.autoAdjust', { from: fmtPct(d.old_threshold ?? d.old), to: fmtPct(d.new_threshold ?? d.new) }) };
    case 'open_aborted_unstable':
    case 'close_aborted_unstable':
      return { icon: '·', tone: 'muted', text: t('events.abortedUnstable', { reason }) };
    case 'open_aborted_stale':
      return { icon: '·', tone: 'muted', text: t('events.abortedStale', { reason }) };
    case 'live_open_failed':
    case 'live_close_failed':
      return { icon: '⚠', tone: 'red', text: t('events.orderRejected', { reason }) };
    case 'live_open_one_sided':
    case 'live_close_one_sided':
      return { icon: '⚠', tone: 'red', text: t('events.oneSided') };
    case 'live_blocked_no_proxy':
      return { icon: '⚠', tone: 'red', text: t('events.blockedNoProxy', { reason: e.reason || t('events.noProxy') }) };
    default:
      // Неизвестный вид: причина от движка уже по-русски — её и показываем,
      // а служебный id уходит в подсказку, а не в текст.
      return { icon: '·', tone: 'muted', text: e.reason || t('events.service'), title: e.kind };
  }
}
